// FOUNDER CORRECTION 2026-09-25 · item 2 — CARD DETAILS ON THE REVIEW PAGE ITSELF.
//
// "/mountain-whistler/booking/review should contain all purchaser/traveller AND real card details on the
// same page, in kit 2.01 design; no offsite or separate checkout step." The Kit's CARD DETAILS step already
// collects purchaser + traveller on that URL; the card used to be taken on a second page (the checkout
// app). Stripe's Payment Element may be mounted in a top-level page — never nested inside another iframe —
// so it now mounts in THIS document, inside the Kit's own Card Details panel (`InpageCardPayment`).
//
// This module is the pure half: what the page may say about an order, decided ONLY from a status the
// server read back. The money rules it keeps are the checkout app's, unchanged:
//   1. SERVER-PRICED — the amount is the order's; the page only checks it equals what it displayed.
//   2. ADDRESS BEFORE MONEY (F9) — country + postal code exist before a PaymentIntent is requested.
//   3. ONLY THE SERVER MAY SAY PAID — a resolved `confirmPayment` is a hand-off, not an outcome.
// The classification and the awaiting-acceptance wording are imported from the SAME modules the
// checkout app reads (`@repo/ui/lib/order-status`, `@repo/ui/lib/order-fulfillment`), not copied.

import { acceptanceCopy, fulfillmentStillLanding, parseFulfillment } from "@repo/ui/lib/order-fulfillment";
import type { DesignCartLine } from "@/components/marketplace/DesignCartLines";
import { orderPhase } from "@repo/ui/lib/order-status";
import type { PurchaseEvent } from "@/lib/analytics-purchase";
import type { HeldOrderFacts } from "@/lib/design-pay";

export const POLL_INTERVAL_MS = 2_000;
export const POLL_ATTEMPTS = 30; // ~60s, then say so rather than guess

const NOT_CHARGED = "Nothing was charged.";

const ADDRESS_KEYS = ["line1", "line2", "city", "state", "postal_code", "country"] as const;
export type BillingAddress = Record<(typeof ADDRESS_KEYS)[number], string>;

/** Only the keys Stripe's `customers.update({ address })` accepts, trimmed and bounded; null unless
 * both country (ISO-2) and postal code are present — the F9 precondition for requesting an intent. */
export function billingAddress(raw: unknown): BillingAddress | null {
  if (!raw || typeof raw !== "object") return null;
  const src = raw as Record<string, unknown>;
  const out = Object.fromEntries(
    ADDRESS_KEYS.map((k) => [k, typeof src[k] === "string" ? src[k].trim().slice(0, 120) : ""]),
  ) as BillingAddress;
  out.country = out.country.toUpperCase();
  if (!/^[A-Z]{2}$/.test(out.country) || out.postal_code === "") return null;
  return out;
}

// ── M4 · PAY AT THE SHOP (SNOWCLUB-STAGING-M4-PAY-AT-SHOP-FE-20261026, wire contract snowclub-m4-pay-at-shop-wire-v1) ─────────
// Founder 18:10 PT: Rentals & Gear are M4 with no deposit — a reservation at the server-accepted price, paid to the shop on arrival.
// The ONLY thing that turns the card, billing address and Stripe off is the server's own typed plan on the quote, read here exactly:
// never the store category, the product name or anything the browser holds. Anything that is not exactly the M3 card plan or
// exactly the M4 zero-due plan is `invalid` and refused by the caller, never guessed into either.

/**
 * The server's zero-percent M4 plan, canonical: nothing is due online. The server total is the full PRE-TAX price (BE contract
 * snowclub-payments-phase1-v3-actual quoteTotals: tax 0); the shop adds the applicable taxes on that full price at the counter.
 */
export interface PayAtShopPlan {
  mode: "M4";
  kind: "pay_at_shop";
  cardRequired: false;
  depositCents: 0;
  dueNowCents: 0;
  /** = the server total in cents: the full pre-tax price (the shop adds the applicable taxes) */
  balanceDueCents: number;
  /** the server's ISO-4217 presentment currency for this booking (CAD source prices, or the server's FX conversion) */
  currency: string;
}

// ── M4-D · DEPOSIT (WO-PAYMENTS-PHASE1-001 P2/P3/P6 + amendment 12:55; BE contract snowclub-payments-phase1-v3-actual) ───────────
// The customer pays a deposit (the server's per-line percent of the PRE-TAX price, commission tax included) by card now; the vendor
// collects the pre-tax balance plus the applicable taxes on the FULL price at the counter. Every figure is the server's: the browser
// checks the block is self-consistent and never computes, rounds, aggregates or defaults a percent, a deposit or a balance.

/** One line's stored deposit terms, exactly as the server states them. */
export interface DepositLineTerms {
  productDocumentId: string;
  depositPercent: number;
  preTaxCents: number;
  depositCents: number;
}

/** The server's M4-D plan, canonical. The quote total IS `dueNowCents` (what the card is authorised/charged now). */
export interface DepositPlan {
  mode: "M4";
  kind: "deposit";
  cardRequired: true;
  /** finite 0..100 (decimals allowed), or null ONLY when the lines' stored percentages differ (then `depositPercentByLine` says them) */
  depositPercent: number | null;
  depositCents: number;
  dueNowCents: number;
  /** the full pre-tax price of the M4-D lines (= the vendor's tax basis) */
  preTaxCents: number;
  /** = preTaxCents - depositCents: what the vendor collects at the counter, BEFORE the taxes on the full price */
  balanceDueCents: number;
  /** the server's ISO-4217 presentment currency (never chosen or converted here) */
  currency: string;
  /** the capture PLAN (new vendor: authorise now, capture on accept · known: capture at order) - never proof that anything was paid */
  capture: "on_accept" | "at_order";
  vendorStatus: "new" | "known";
  depositPercentByLine?: DepositLineTerms[];
}

export type PaymentPlanRead =
  | { kind: "card" }
  | { kind: "pay_at_shop"; plan: PayAtShopPlan }
  | { kind: "deposit"; plan: DepositPlan }
  | { kind: "invalid" };

const plainObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
const cents = (v: unknown): v is number => typeof v === "number" && Number.isSafeInteger(v) && v >= 0;
/** A server-stated ISO-4217 code, upper case exactly as the contract writes it ("CAD", "USD"). Anything else is not a currency. */
export const isCurrencyCode = (v: unknown): v is string => typeof v === "string" && /^[A-Z]{3}$/.test(v);
const percent = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= 100;

/** The per-line terms, or null when any line is malformed or they do not add up to the plan's own deposit and pre-tax price. */
function depositLinesOf(raw: unknown, depositCents: number, preTaxCents: number): DepositLineTerms[] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const lines: DepositLineTerms[] = [];
  for (const l of raw as unknown[]) {
    if (!plainObject(l) || typeof l.productDocumentId !== "string" || l.productDocumentId === "" || !percent(l.depositPercent) || !cents(l.preTaxCents) || !cents(l.depositCents) || l.depositCents > l.preTaxCents) return null;
    lines.push({ productDocumentId: l.productDocumentId, depositPercent: l.depositPercent, preTaxCents: l.preTaxCents, depositCents: l.depositCents });
  }
  const sum = (k: "depositCents" | "preTaxCents") => lines.reduce((n, l) => n + l[k], 0);
  return sum("depositCents") === depositCents && sum("preTaxCents") === preTaxCents ? lines : null;
}

/** The exact M4-D block, judged on its own (no total): null for anything that is not exactly the contract shape. */
function depositPlanOf(raw: Record<string, unknown>): DepositPlan | null {
  const { depositCents, preTaxCents, depositPercent: pct } = raw;
  if (raw.mode !== "M4" || raw.kind !== "deposit" || raw.cardRequired !== true || !isCurrencyCode(raw.currency)) return null;
  if (!cents(depositCents) || depositCents === 0 || !cents(preTaxCents) || depositCents > preTaxCents) return null;
  if (raw.dueNowCents !== depositCents || raw.balanceDueCents !== preTaxCents - depositCents) return null;
  if (raw.authorizeCents !== undefined && raw.authorizeCents !== depositCents) return null;
  if (raw.vendorTaxBasisCents !== undefined && raw.vendorTaxBasisCents !== preTaxCents) return null;
  if (raw.commissionTaxCents !== undefined && (!cents(raw.commissionTaxCents) || raw.commissionTaxCents > depositCents)) return null;
  if (raw.capture !== "on_accept" && raw.capture !== "at_order") return null;
  if (raw.vendorStatus !== "new" && raw.vendorStatus !== "known") return null;
  const lines = raw.depositPercentByLine === undefined ? undefined : depositLinesOf(raw.depositPercentByLine, depositCents, preTaxCents);
  if (lines === null) return null;
  if (pct === null) {
    // null means the stored percentages differ: the lines must say so, never an aggregate made up here
    if (!lines || new Set(lines.map((l) => l.depositPercent)).size < 2) return null;
  } else if (!percent(pct) || pct === 0 || lines?.some((l) => l.depositPercent !== pct)) {
    return null;
  }
  return {
    mode: "M4",
    kind: "deposit",
    cardRequired: true,
    depositPercent: pct,
    depositCents,
    dueNowCents: depositCents,
    preTaxCents,
    balanceDueCents: preTaxCents - depositCents,
    currency: raw.currency,
    capture: raw.capture,
    vendorStatus: raw.vendorStatus,
    ...(lines ? { depositPercentByLine: lines } : {}),
  };
}

/** A stored or status-read deposit plan re-read: the same exact shape. */
export function isDepositPlan(v: unknown): v is DepositPlan {
  return plainObject(v) && depositPlanOf(v) !== null;
}

const dollars = (c: number) => `$${(c / 100).toFixed(2)}`;
/** An amount in the plan's own currency: CAD keeps the existing "$12.00" copy; any other server currency is named by its own symbol. */
const amountIn = (c: number, currency: string) =>
  currency === "CAD" ? dollars(c) : new Intl.NumberFormat("en-CA", { style: "currency", currency }).format(c / 100);
/**
 * The M4-D terms said as what they are, ONLY from the server plan: the deposit due now, the full pre-tax price, and the balance the
 * vendor collects plus the applicable taxes on the full price. The third line states the capture PLAN, as a plan.
 */
export function depositTermsLines(plan: DepositPlan): string[] {
  return [
    `Deposit: ${amountIn(plan.dueNowCents, plan.currency)} ${plan.currency}, including deposit taxes. Full price before tax: ${amountIn(plan.preTaxCents, plan.currency)}.`,
    `Balance at the shop: ${amountIn(plan.balanceDueCents, plan.currency)} + applicable taxes on the full ${amountIn(plan.preTaxCents, plan.currency)} price, paid to the shop on arrival.`,
    plan.capture === "on_accept"
      ? "Submitting places a hold for the deposit on your card. It is charged only if the shop accepts. If no shop accepts, nothing is charged."
      : "Submitting charges the deposit. If no shop can take your booking, the deposit is refunded in full.",
  ];
}

// ── M4-D money facts on a status read (contract: depositStatus + booleans govern the copy; fulfillment is separate) ──────────────
export type DepositStatus = "none" | "unconfirmed" | "authorized" | "captured" | "refund_pending" | "refunded" | "cancelled" | "failed";
export type FulfillmentStatus = "pending_acceptance" | "declined" | "offer_open" | "confirmed" | "closed";
const DEPOSIT_STATUSES: readonly DepositStatus[] = ["none", "unconfirmed", "authorized", "captured", "refund_pending", "refunded", "cancelled", "failed"];
const FULFILLMENT_STATUSES: readonly FulfillmentStatus[] = ["pending_acceptance", "declined", "offer_open", "confirmed", "closed"];

export interface DepositFacts {
  plan: DepositPlan;
  depositStatus: DepositStatus;
  depositCaptured: boolean;
  authorizationActive: boolean;
  refundPending: boolean;
  reoffered: boolean;
  fulfillmentStatus: FulfillmentStatus;
}

/**
 * The deposit facts of one status read, or null (no deposit claim at all) unless the read carries the exact M4-D block AND money facts
 * that agree with each other: `authorizationActive` only for `authorized`, `depositCaptured` only for `captured`/`refund_pending`,
 * `refundPending` only for `refund_pending`. The capture plan never stands in for any of them.
 */
export function depositFactsOf(read: { payment?: unknown } | null | undefined): DepositFacts | null {
  const raw = read?.payment;
  if (!plainObject(raw)) return null;
  const plan = depositPlanOf(raw);
  const { depositStatus: s, depositCaptured, authorizationActive, refundPending, reoffered, fulfillmentStatus: f } = raw;
  if (!plan || typeof s !== "string" || !(DEPOSIT_STATUSES as readonly string[]).includes(s)) return null;
  if (typeof f !== "string" || !(FULFILLMENT_STATUSES as readonly string[]).includes(f)) return null;
  if (typeof depositCaptured !== "boolean" || typeof authorizationActive !== "boolean" || typeof refundPending !== "boolean" || typeof reoffered !== "boolean") return null;
  if (authorizationActive !== (s === "authorized") || depositCaptured !== (s === "captured" || s === "refund_pending") || refundPending !== (s === "refund_pending")) return null;
  return { plan, depositStatus: s as DepositStatus, depositCaptured, authorizationActive, refundPending, reoffered, fulfillmentStatus: f as FulfillmentStatus };
}

/** A zero-percent M4 order's facts on a status read: nothing is ever held or charged; only the shop's fulfillment moves. */
export interface PayAtShopFacts {
  plan: PayAtShopPlan;
  preTaxCents: number;
  depositPercent?: number | null;
  depositPercentByLine?: DepositLineTerms[];
  /** BE marketplace: `none`, or `cancelled` once a no-taker closed the order */
  depositStatus: "none" | "cancelled";
  depositState?: "reoffered" | "exhausted" | null;
  fulfillmentStatus: FulfillmentStatus;
  reoffered: boolean;
  vendorAccepted?: boolean;
}

/**
 * The exact zero-percent M4 facts of one status read (BE `publicMarketplace` over an all-zero `marketplaceSummary`), or null. The `kind`
 * tag is never enough: every money field must be zero (deposit, due now, authorised, included commission tax), no card is required, the
 * balance is the whole pre-tax price in the server's stated currency, and the money facts say nothing was ever held or taken — `depositStatus` `none` (or
 * `cancelled` only on a closed order), `depositCaptured` / `authorizationActive` / `refundPending` all false. A captured, authorised or
 * refunded deposit, a card requirement, a positive amount or a missing / malformed field is not this shape.
 */
export function payAtShopFactsOf(read: { payment?: unknown } | null | undefined): PayAtShopFacts | null {
  const raw = read?.payment;
  if (!plainObject(raw)) return null;
  const { preTaxCents: pre, depositStatus: s, fulfillmentStatus: f, reoffered, depositState, vendorAccepted, depositPercent: pct } = raw;
  if (raw.mode !== "M4" || raw.kind !== "pay_at_shop" || raw.cardRequired !== false || !isCurrencyCode(raw.currency)) return null;
  if (raw.depositCents !== 0 || raw.dueNowCents !== 0) return null;
  if (raw.authorizeCents !== undefined && raw.authorizeCents !== 0) return null;
  if (raw.commissionTaxCents !== undefined && raw.commissionTaxCents !== 0) return null;
  if (!cents(pre) || pre === 0 || raw.balanceDueCents !== pre) return null;
  if (raw.vendorTaxBasisCents !== undefined && raw.vendorTaxBasisCents !== pre) return null;
  const lines = raw.depositPercentByLine === undefined ? undefined : depositLinesOf(raw.depositPercentByLine, 0, pre);
  if (lines === null) return null;
  if (pct !== undefined && pct !== null && !percent(pct)) return null;
  if (s !== "none" && s !== "cancelled") return null;
  if (typeof f !== "string" || !(FULFILLMENT_STATUSES as readonly string[]).includes(f)) return null;
  if (raw.depositCaptured !== false || raw.authorizationActive !== false || raw.refundPending !== false || typeof reoffered !== "boolean") return null;
  if (s === "cancelled" && f !== "closed") return null;
  if (depositState !== undefined && depositState !== null && depositState !== "reoffered" && !(depositState === "exhausted" && f === "closed")) return null;
  if (vendorAccepted !== undefined && (typeof vendorAccepted !== "boolean" || (f === "confirmed" && !vendorAccepted))) return null;
  return {
    plan: { mode: "M4", kind: "pay_at_shop", cardRequired: false, depositCents: 0, dueNowCents: 0, balanceDueCents: pre, currency: raw.currency },
    preTaxCents: pre,
    ...(pct !== undefined ? { depositPercent: pct } : {}),
    ...(lines ? { depositPercentByLine: lines } : {}),
    depositStatus: s,
    ...(depositState !== undefined ? { depositState } : {}),
    fulfillmentStatus: f as FulfillmentStatus,
    reoffered,
    ...(vendorAccepted !== undefined ? { vendorAccepted } : {}),
  };
}

/** The facts re-emitted in canonical shape (what `payAtShopFactsOf` accepts back): only validated fields, nothing computed. */
export function payAtShopBlock(f: PayAtShopFacts): Record<string, unknown> {
  const { plan, ...rest } = f;
  return { ...plan, authorizeCents: 0, commissionTaxCents: 0, vendorTaxBasisCents: f.preTaxCents, ...rest, depositCaptured: false, authorizationActive: false, refundPending: false };
}

const M4_FACT_KEYS = ["depositStatus", "depositCaptured", "authorizationActive", "refundPending", "reoffered", "fulfillmentStatus", "depositState", "balanceDueCents", "preTaxCents", "depositPercent", "depositPercentByLine", "capture", "vendorStatus"];

/** The exact M3 card-hold block (BE `quotePaymentBlock`): a full-total authorisation, no deposit, no amount due now, and no M4 fact at all. */
export function isM3CardHold(raw: unknown): boolean {
  if (!plainObject(raw)) return false;
  if (raw.mode !== "M3" || raw.kind !== "card_hold" || raw.cardRequired !== true || !isCurrencyCode(raw.currency)) return false;
  if (!cents(raw.authorizeCents) || raw.authorizeCents === 0) return false;
  if ((raw.depositCents !== undefined && raw.depositCents !== 0) || (raw.dueNowCents !== undefined && raw.dueNowCents !== 0)) return false;
  return !M4_FACT_KEYS.some((k) => k in raw);
}

/** One payment block, judged in full by the exact validators above; its `mode` / `kind` tag alone is never a classification. */
export type PaymentBlock =
  | { kind: "deposit"; facts: DepositFacts }
  | { kind: "pay_at_shop"; facts: PayAtShopFacts }
  | { kind: "m3" }
  | { kind: "invalid" };

/**
 * THE classification of a payment block, used for a status projection and for the same block on the customer's own list row alike:
 * exact M4-D deposit facts, exact zero-percent M4 facts, the exact M3 card hold, or `invalid` for anything else (a tag over the wrong
 * shape, a missing or malformed field, a non-object).
 */
export function classifyPaymentBlock(raw: unknown): PaymentBlock {
  const deposit = depositFactsOf({ payment: raw });
  if (deposit) return { kind: "deposit", facts: deposit };
  const atShop = payAtShopFactsOf({ payment: raw });
  if (atShop) return { kind: "pay_at_shop", facts: atShop };
  return isM3CardHold(raw) ? { kind: "m3" } : { kind: "invalid" };
}

/** The done screen's money word, from the server's depositStatus only. */
export type MoneyState = "authorised" | "paid" | "refund_pending" | "refunded" | "released" | "not_taken" | "unknown";
const MONEY_OF: Record<DepositStatus, MoneyState> = {
  authorized: "authorised",
  captured: "paid",
  refund_pending: "refund_pending",
  refunded: "refunded",
  cancelled: "released",
  failed: "not_taken",
  none: "unknown",
  unconfirmed: "unknown",
};

/** The receipt for one read: the MONEY sentence from the actual deposit facts, then the separate fulfillment sentence. */
export function depositReceiptCopy(facts: DepositFacts): { kicker: string; lead: string } {
  const { plan } = facts;
  const deposit = `${amountIn(plan.depositCents, plan.currency)} deposit`;
  const balance = `${amountIn(plan.balanceDueCents, plan.currency)} + applicable taxes on the full ${amountIn(plan.preTaxCents, plan.currency)} price`;
  const [kicker, money] = ((): [string, string] => {
    switch (facts.depositStatus) {
      case "authorized":
        return ["Deposit authorised", `Your ${deposit} is authorised, not charged. It is charged only when a shop accepts.`];
      case "captured":
        return ["Deposit paid", `Your ${deposit} is paid.`];
      case "refund_pending":
        return ["Refund in progress", `Your ${deposit} was paid and its full refund is in progress.`];
      case "refunded":
        return ["Deposit refunded", `Your ${deposit} was refunded in full.`];
      case "cancelled":
        return ["Authorisation released", `The ${deposit} authorisation was released; nothing was charged.`];
      case "failed":
        return ["Deposit not taken", `The ${deposit} did not go through; nothing was charged.`];
      default:
        return ["Deposit not confirmed yet", `We can't confirm your ${deposit} yet. Do not pay again — check My Trips.`];
    }
  })();
  const fulfillment =
    facts.reoffered || facts.fulfillmentStatus === "offer_open"
      ? "The first shop declined, so your booking was offered to the next shop on the same terms."
      : facts.fulfillmentStatus === "confirmed"
        ? `The shop accepted: you pay ${balance} at the shop.`
        : facts.fulfillmentStatus === "pending_acceptance"
          ? `Waiting for the shop to accept; then you pay ${balance} at the shop.`
          : facts.fulfillmentStatus === "declined"
            ? "The shop declined this booking."
            : "This booking is closed.";
  return { kicker, lead: `${money} ${fulfillment}` };
}


/**
 * The quote's `payment` block, judged against the quote's own total and currency. Absent = a backend that predates the block: the
 * card path with all of its guards (never a reason to skip the card). M3 = the card path. M4 is accepted only as the exact zero-due
 * shape whose balance is the quote's total to the cent, in the quote's own currency; a deposit, an amount due now, a card requirement or any mismatch is
 * `invalid`.
 */
export function readPaymentPlan(raw: unknown, total: unknown, currency: unknown): PaymentPlanRead {
  if (raw === undefined) return { kind: "card" };
  if (!plainObject(raw) || typeof total !== "number" || !Number.isFinite(total)) return { kind: "invalid" };
  const totalCents = Math.round(total * 100);
  if (raw.mode === "M3") {
    if (raw.kind !== "card_hold" || raw.cardRequired !== true) return { kind: "invalid" };
    if (raw.authorizeCents !== undefined && raw.authorizeCents !== totalCents) return { kind: "invalid" };
    // the hold is in the quote's currency or it is not this quote's hold (a block that omits it predates the field)
    if (raw.currency !== undefined && raw.currency !== currency) return { kind: "invalid" };
    return { kind: "card" };
  }
  if (raw.mode !== "M4") return { kind: "invalid" };
  if (raw.kind === "deposit") {
    // M4-D: the quote total is exactly what the card is charged/authorised now - the deposit - in the quote's own currency (FE contract Q1)
    const plan = depositPlanOf(raw);
    return plan?.dueNowCents === totalCents && plan.currency === currency ? { kind: "deposit", plan } : { kind: "invalid" };
  }
  const exact =
    raw.kind === "pay_at_shop" &&
    raw.cardRequired === false &&
    raw.depositCents === 0 &&
    raw.dueNowCents === 0 &&
    typeof raw.balanceDueCents === "number" &&
    Number.isSafeInteger(raw.balanceDueCents) &&
    raw.balanceDueCents > 0 &&
    raw.balanceDueCents === totalCents &&
    isCurrencyCode(raw.currency) &&
    raw.currency === currency;
  if (!exact) return { kind: "invalid" };
  return {
    kind: "pay_at_shop",
    plan: { mode: "M4", kind: "pay_at_shop", cardRequired: false, depositCents: 0, dueNowCents: 0, balanceDueCents: raw.balanceDueCents as number, currency: raw.currency as string },
  };
}

/** A stored plan re-read (an order record from sessionStorage): the same exact shape, against its own balance. */
export function isPayAtShopPlan(v: unknown): v is PayAtShopPlan {
  return plainObject(v) && typeof v.balanceDueCents === "number" && readPaymentPlan(v, v.balanceDueCents / 100, v.currency).kind === "pay_at_shop";
}

// WO-PAYMENTS-PHASE1-001 P1 (Root contract answer Q6): M4 operates in production, so the browser no longer keeps a Stripe-TEST-only line
// for pay-at-shop. Where M4 is not admitted the BACKEND refuses it (`payment_mode_not_executable`), and that refusal is shown as before.

/** The order statuses an M4 create (or its idempotent replay) may return; anything else for an M4 quote is refused. */
export const PAY_AT_SHOP_CREATE_STATUSES = ["pending_acceptance", "pending_payment", "cancelled"] as const;

/** Where an M4 reservation stands, from the server's own read. Never paid, never card-authorised. */
export type ReservationStatus = "requested" | "accepted" | "declined";
export interface ReservationFacts {
  plan: PayAtShopPlan;
  status: ReservationStatus;
}

/**
 * One server read of an M4 order, judged (contract lifecycle): `pending_acceptance` = requested; `pending_payment` WITH inventory
 * `confirmed` = the shop accepted, balance due at the shop; `cancelled` = declined or withdrawn. A card attached to it, `paid`, a
 * draft, a hold or anything else is not an M4 reservation state: null, and the caller fails closed.
 *
 * The status projection, when it was read, must agree (BE `decideMarketplace` / `closeNoTaker`): an exact pay-at-shop block whose
 * balance is the reservation's own, with `requested` only while fulfillment is `pending_acceptance`, `accepted` only once it is
 * `confirmed`, `declined` only once it is `closed`, and nothing ever held or taken. A deposit, M3 or malformed block is not a
 * reservation (null). A projection that could not be read, or a backend that sends none, leaves the order's own status words.
 */
export function reservationVerdict(read: OrderStatusRead | null | undefined, plan?: PayAtShopPlan): ReservationStatus | null {
  if (!read || read.cardAuthorized === true) return null;
  const projection = read.paymentProjection;
  if (projection === "deposit" || projection === "not_deposit" || projection === "invalid") return null;
  if (projection === "pay_at_shop") {
    const facts = payAtShopFactsOf(read);
    if (!facts || (plan && facts.plan.balanceDueCents !== plan.balanceDueCents)) return null;
    const untouched = facts.depositStatus === "none";
    if (read.orderStatus === "pending_acceptance") return untouched && facts.fulfillmentStatus === "pending_acceptance" ? "requested" : null;
    if (read.orderStatus === "pending_payment" && read.inventoryStatus === "confirmed") return untouched && facts.fulfillmentStatus === "confirmed" ? "accepted" : null;
    if (read.orderStatus === "cancelled") return facts.fulfillmentStatus === "closed" ? "declined" : null;
    return null;
  }
  if (read.orderStatus === "pending_acceptance") return "requested";
  if (read.orderStatus === "pending_payment" && read.inventoryStatus === "confirmed") return "accepted";
  if (read.orderStatus === "cancelled") return "declined";
  return null;
}

const shopAmount = (plan: PayAtShopPlan) => `${amountIn(plan.balanceDueCents, plan.currency)} ${plan.currency} + applicable taxes on the full price`;

/**
 * A reservation said as what it is, on the existing reason path (no `authorized`, no `orderId`), so it can never satisfy the design's
 * card-authorised confirmation gate, never ends the attempt and never emits a purchase. `retry: false`: a second press replays the
 * same order, it never books twice.
 */
export function reservationOutcome(facts: ReservationFacts): InpageResult {
  const reason =
    facts.status === "requested"
      ? `Reservation requested. The shop has to accept it before your booking is confirmed. Nothing was charged — you pay ${shopAmount(facts.plan)} at the shop on arrival.`
      : facts.status === "accepted"
        ? `The shop accepted your reservation. Nothing was charged online — you pay ${shopAmount(facts.plan)} at the shop on arrival.`
        : `This reservation is cancelled — the shop declined it or it was withdrawn. ${NOT_CHARGED}`;
  return { booked: false, retry: false, reserved: facts, reason };
}

/** An M4 order whose server read is not an M4 reservation state (or a create that did not return one): held, never paid. */
export const RESERVATION_UNCONFIRMED: InpageResult = {
  booked: false,
  retry: false,
  reason: `We can't confirm this reservation right now, so no card is asked for. ${NOT_CHARGED} Do not book again — check My Trips.`,
};

/**
 * A replayed order that is already past its draft can only be shown as card-authorised with the server's own card evidence: a
 * `pending_acceptance` / `paid` (or accepted pay-at-shop) read WITHOUT a PaymentIntent attached is not this card flow's order — it
 * is a reservation created under a stale card quote, or something else. It is held, never authorised: no attempt end, no cart
 * change, no purchase event, no intent, and the same order replays (My Trips recovers it).
 */
export const CARD_UNCONFIRMED: InpageResult = {
  booked: false,
  retry: false,
  reason: `This booking was not placed as a card payment, so we can't show it as paid or authorised and no card is asked for. ${NOT_CHARGED} Do not book again — check My Trips.`,
};

/** A server read that says the order moved on WITHOUT any card attached (the stale-quote M4 reservation and its later states). */
export function settledWithoutCard(read: OrderStatusRead | null | undefined): boolean {
  if (!read || read.cardAuthorized === true) return false;
  const phase = orderPhase(read.orderStatus);
  return phase === "paid" || phase === "awaiting_acceptance" || (read.orderStatus === "pending_payment" && read.inventoryStatus === "confirmed");
}

/**
 * THE SHARED SETTLEMENT BOUNDARY (every entry: the in-page pay, the poll, "Check payment status", a reload's reconciliation and a
 * redirect return all settle through `judgeRead`). A read that moved on (paid, awaiting acceptance, or an accepted M4 hold) is
 * settled as card-authorised / paid ONLY when the server positively says a card is attached (`cardAuthorized === true`, from the
 * order's PaymentIntent). `false` is a pay-at-shop reservation or something else; a MISSING field is unknown, never authorised. This
 * holds whatever the caller remembers about the order, so it does not depend on the optional create-time `cardEvidenceRequired`.
 */
export function cardEvidenceMissing(read: OrderStatusRead | null | undefined): boolean {
  return settledWithoutCard(read);
}

/**
 * M4 · "Start a new checkout" after a DEFINITE decline. One fresh server read must still say `cancelled` (declined) for this
 * reservation; only then is the old order's record settled (its cart lines stay — nothing was bought) and ONLY the attempt that
 * created it ended, so the next order request is a new order. Requested, accepted, unreadable or unconfirmed reads change nothing:
 * the lock and its replay safety stay, and the caller shows the read. Never an order, intent, card or purchase from here.
 */
export async function startFreshAfterDecline({
  orderId,
  plan,
  snapshot,
  deps,
}: {
  orderId: string;
  plan: PayAtShopPlan;
  snapshot: OrderCartSnapshot | null;
  deps: Pick<InpagePaymentDeps, "readStatus"> & { endAttemptIf: (attemptId: string | null) => void; markSettled: (snapshot: OrderCartSnapshot) => void };
}): Promise<{ fresh: true } | { fresh: false; result: InpageResult }> {
  const r = await checkReservation({ orderId, plan, deps });
  if ("waiting" in r) return { fresh: false, result: RESERVATION_UNCONFIRMED };
  if (!("reserved" in r) || r.reserved?.status !== "declined") return { fresh: false, result: r };
  // the record must be THIS order's: without it the owning attempt is unknown and nothing is ended (the decline stays shown)
  if (snapshot?.documentId !== orderId) return { fresh: false, result: r };
  deps.markSettled(snapshot);
  deps.endAttemptIf(snapshot.attemptId);
  return { fresh: true };
}

/** "Check reservation status": ONE read, judged as an M4 reservation. It never creates, pays or replays anything. */
export async function checkReservation({
  orderId,
  plan,
  deps,
}: {
  orderId: string;
  plan: PayAtShopPlan;
  deps: Pick<InpagePaymentDeps, "readStatus">;
}): Promise<InpageResult | { waiting: true }> {
  const read = await deps.readStatus(orderId);
  if (!read) return { waiting: true };
  const status = reservationVerdict(read, plan);
  return status ? reservationOutcome({ plan, status }) : RESERVATION_UNCONFIRMED;
}

/** The server's PaymentIntent must authorize exactly what the Pay button showed; anything else is refused. */
export function amountMatches(amountCents: unknown, displayedTotal: number): boolean {
  return typeof amountCents === "number" && Number.isInteger(amountCents) && amountCents === Math.round(displayedTotal * 100);
}

/** What the design is told after the card was handed to Stripe. `authorized` is the ONLY success shape and
 * `booked` is never true: an authorized order is waiting on the operator, it is not a confirmed booking.
 * `pending` is true unless the server read the literal `paid` — the design's done copy may promise the trip
 * only when it is false. */
export type InpageResult =
  | {
      booked: false;
      authorized: true;
      /** exactly: the card is authorised, not charged, and the order awaits the operator (the design's "not charged" copy keys on it) */
      pending: boolean;
      orderId: string;
      paid: string;
      items: number;
      kicker: string;
      lead: string;
      /** M4-D / unknown projection only: the booking is NOT confirmed by the operator, whatever the money state (no travel promise) */
      awaiting?: boolean;
      /** M4-D / unknown projection only: the server's money state, from depositStatus and its flags (never the capture plan) */
      money?: MoneyState;
    }
  | {
      booked: false;
      reason: string;
      retry: boolean;
      /** present only for a server-held native order: a truthful held-only notice, never a payment or a booking */
      held?: HeldOrderFacts;
      /** present only for an M4 pay-at-shop reservation the server read back: nothing charged, never a payment or a confirmed booking */
      reserved?: ReservationFacts;
    };

/**
 * A server-placed HOLD, said as what it is: the quantity is held until the SERVER'S expiry, nothing was charged, the trip is not
 * confirmed. It is a notice on the existing reason path (no `authorized`, no `orderId`), so it can never satisfy the design's
 * authorized+orderId confirmation gate, and it never ends the attempt, clears the cart or emits a purchase.
 */
function heldOutcome(held: HeldOrderFacts): InpageResult {
  return {
    booked: false,
    retry: false,
    held,
    reason: `Your ${held.heldQuantity === 1 ? "seat is" : `${held.heldQuantity} seats are`} held until ${held.holdExpiresAt}. No payment was taken and your trip is not confirmed.`,
  };
}

export type PollVerdict =
  | { kind: "keep-polling" }
  | { kind: "settled"; result: InpageResult }
  | { kind: "failed"; result: InpageResult };

/**
 * One read of `/api/orders/<id>/status`, judged. `sightings` counts consecutive `pending_acceptance`
 * reads so the no-objection record gets its grace polls (lib `fulfillmentStillLanding`) before the copy
 * is believed — the webhook writes the status first and the operator request second.
 */
export function judgeStatus({
  status,
  fulfillment,
  orderRef,
  paid,
  items,
  sightings,
  attempt,
}: {
  status: string | null | undefined;
  fulfillment: unknown;
  orderRef: string;
  paid: string;
  items: number;
  sightings: number;
  attempt: number;
}): PollVerdict {
  const phase = orderPhase(status);
  if (phase === "paid") {
    return {
      kind: "settled",
      result: { booked: false, authorized: true, pending: false, orderId: orderRef, paid, items, kicker: "Paid", lead: "This order is paid. You'll get an email with the details." },
    };
  }
  if (phase === "awaiting_acceptance") {
    if (attempt < POLL_ATTEMPTS && fulfillmentStillLanding({ status, fulfillment, justAuthorized: true, sightings })) {
      return { kind: "keep-polling" };
    }
    const copy = acceptanceCopy({ fulfillment: parseFulfillment(fulfillment), cardAuthorized: true });
    return {
      kind: "settled",
      result: { booked: false, authorized: true, pending: true, orderId: orderRef, paid, items, kicker: "Authorised", lead: `${copy.heading}. ${copy.body}` },
    };
  }
  if (phase === "payment_failed") {
    return {
      kind: "failed",
      result: {
        booked: false,
        retry: true,
        reason: `That payment attempt did not go through, so this order has not been paid. ${NOT_CHARGED} You can try again with a different card.`,
      },
    };
  }
  if (phase === "closed") {
    // Printed verbatim, never relabelled into an outcome it is not.
    return { kind: "failed", result: { booked: false, retry: false, reason: `This order is ${status}. ${NOT_CHARGED}` } };
  }
  return { kind: "keep-polling" };
}

/** The poll ran out without the server moving: true, and not a claim that anything failed. */
export const TIMED_OUT: InpageResult = {
  booked: false,
  retry: false,
  reason:
    "Your card was submitted and we're still waiting for our system to confirm it. Do not pay again — the order will update in My Trips and we'll email you.",
};

/** What `/api/orders/<id>/status` returns — status fields plus the server's own money for the order. */
export interface OrderStatusRead {
  orderStatus?: string | null;
  /** the order's inventory state (M4: `confirmed` once the shop accepted) — a status word only */
  inventoryStatus?: string | null;
  legacyId?: string | null;
  fulfillment?: unknown;
  subTotal?: number | null;
  tax?: number | null;
  total?: number | null;
  /** the order's frozen presentment currency (status BFF, upper-case ISO); absent = a CAD order */
  currency?: string;
  /** a PaymentIntent is attached to the order (the id itself never reaches the browser) */
  cardAuthorized?: boolean;
  items?: { id: string; name: string; quantity: number; price: number; category?: string; brand?: string }[];
  /** M4-D · the order's deposit terms + money/fulfillment facts (BE `GET /api/orders/:id/status`), present only when the status BFF validated them */
  payment?: unknown;
  /** what the status BFF learned from the backend's owner-only payment projection (absent on reads that predate it = legacy) */
  paymentProjection?: PaymentProjection;
}

/**
 * `deposit` = exact M4-D facts in `payment`; `pay_at_shop` = exact zero-percent M4 facts in `payment` (nothing held or charged; the
 * shop's fulfillment only); `not_deposit` = the exact M3 card-hold block, or a projection that is missing or unreadable on an order
 * whose own list row carries that EXACT block (never its `mode` tag); `absent` = neither the projection nor the list row carries any
 * payment block (the backend's M3 / legacy status read); `invalid` = a payment block, on either, that is not exactly one of those shapes,
 * or a projection and list row that disagree on the kind; `unavailable` = the projection could not be read and the list row proves no
 * M3. A card read that is `invalid` / `unavailable` / `pay_at_shop` (a card
 * order the server calls pay-at-shop is a contradiction), or `deposit` whose facts fail here, makes the money state unknown; M3 is
 * never made unknown for merely lacking M4 fields.
 */
export type PaymentProjection = "deposit" | "pay_at_shop" | "not_deposit" | "absent" | "invalid" | "unavailable";

/**
 * FOUNDER RULING 2026-09-30 — a card-authorized order in `pending_acceptance` IS a GA4 purchase for analytics,
 * although nothing is captured yet; so is `paid`. The event is built ONLY from the server's read:
 *   · the status is literally `pending_acceptance` or `paid` — statuses only the signed webhook writes. A draft,
 *     `pending_payment`, `payment_failed`, a closed order and a timed-out poll build nothing;
 *   · a PaymentIntent is attached (`cardAuthorized`) — the in-page card flow, not an order that reached the
 *     status some other way;
 *   · `transactionId` is the order's `documentId`: the id this flow created and polls, fixed from creation.
 *     NOT `legacyId`, which can appear between two reads and would count the same order twice. A blank id
 *     builds nothing — GA4 needs a nonempty `transaction_id` to de-duplicate on;
 *   · `value` is GA4's definition — the sum of the order's own lines at their server UNIT cost × quantity, tax
 *     EXCLUDED (developers.google.com/analytics/devguides/collection/ga4/ecommerce), with the server's `tax`
 *     sent separately. NOT `total`, which is tax-inclusive and would overstate every taxed order's revenue.
 *     The lines must be complete and self-consistent: every line a finite price ≥ 0 and a positive integer
 *     quantity, and their sum equal to the server's `subTotal` to the cent (the status route drops a malformed
 *     line, so a short sum means a missing line). Anything else → no event: an unpriced or mis-priced purchase
 *     is worse than a missing one.
 * `pending_acceptance` → `paid` is the SAME transaction id, so the later paid read is refused as a duplicate by
 * `emitPurchaseOnce` (as is every reload, redirect return and replayed poll of the same order).
 */
export function inpagePurchase(documentId: string, read: OrderStatusRead | null | undefined): PurchaseEvent | null {
  if (typeof documentId !== "string" || documentId.trim() === "") return null;
  if (!read || (read.orderStatus !== "pending_acceptance" && read.orderStatus !== "paid")) return null;
  if (read.cardAuthorized !== true) return null;
  const valid = (n: unknown): n is number => typeof n === "number" && Number.isFinite(n);
  if (!valid(read.total) || read.total <= 0) return null; // no authorized amount → not a priced order
  const items = read.items ?? [];
  if (!items.length) return null;
  if (!items.every((i) => valid(i.price) && i.price >= 0 && Number.isInteger(i.quantity) && i.quantity > 0)) return null;
  const cents = items.reduce((sum, i) => sum + Math.round(i.price * 100) * i.quantity, 0);
  if (cents <= 0 || !valid(read.subTotal) || cents !== Math.round(read.subTotal * 100)) return null;
  // the ORDER's own currency (founder checkout request 2026-10-27): absent is a CAD order; a malformed one builds nothing
  const currency = read.currency ?? "CAD";
  if (!isCurrencyCode(currency)) return null;
  return {
    transactionId: documentId,
    value: cents / 100,
    currency,
    ...(valid(read.tax) && read.tax >= 0 ? { tax: read.tax } : {}),
    items: items.map((i) => ({
      item_id: i.id,
      item_name: i.name,
      quantity: i.quantity,
      price: i.price,
      ...(i.brand ? { item_brand: i.brand } : {}),
      ...(i.category ? { item_category: i.category } : {}),
    })),
  };
}

/** Everything `runInpagePayment` touches, injected — the component wires Stripe + fetch; tests wire fakes. */
export interface InpagePaymentDeps {
  /** Payment Element validation (`elements.submit()`); an error message, or null when the card is complete */
  validateCard: () => Promise<string | null>;
  /** `createDesignOrder` — the shared server-priced draft order */
  createOrder: () => Promise<
    | {
        ok: true;
        documentId: string;
        clientTotal: number;
        held?: HeldOrderFacts;
        /** an M4 order: the server plan and the create's own status */
        reserved?: { plan: PayAtShopPlan; orderStatus: string };
        /** a card-quote replay that came back past its draft: authorised only with the server's own card evidence */
        cardEvidenceRequired?: boolean;
      }
    | { ok: false; reason: string }
  >;
  /** POST /api/orders/<id>/payment-intent */
  requestIntent: (documentId: string, address: BillingAddress) => Promise<{ ok: true; clientSecret: string; amountCents: unknown } | { ok: false; reason: string }>;
  /** `elements.update({ amount })` + re-validate (it stands in for `validateCard`); an error message, or null */
  resizeElement: (amountCents: number) => Promise<string | null>;
  /** Stripe's own status for the intent (`stripe.retrievePaymentIntent`), or null when it could not be read */
  intentStatus: (clientSecret: string) => Promise<string | null>;
  /** `stripe.confirmPayment(...)` with `redirect: "if_required"`; an error message, or null (handed off) */
  confirm: (clientSecret: string, documentId: string) => Promise<string | null>;
  /** GET /api/orders/<id>/status → the server's read, or null when it could not be read this time */
  readStatus: (documentId: string) => Promise<OrderStatusRead | null>;
  sleep: (ms: number) => Promise<void>;
  /** ends the idempotency attempt — called ONLY once the server has settled on pending_acceptance / paid */
  endAttempt: () => void;
  /** GA4 `purchase` (`emitPurchaseOnce`) — offered ONLY a settled pending_acceptance / paid read (`inpagePurchase`) */
  emitPurchase?: (event: PurchaseEvent) => void;
  /** GA4 `add_payment_info` — offered once per visit, right before a NEW confirm; carries nothing (the caller fixes the payload) */
  emitPaymentInfo?: () => void;
}

/** A PaymentIntent in one of these has already had a card handed to it: confirming again is never right. */
const HANDED_OFF = ["requires_capture", "processing", "succeeded"];

export interface InpagePaymentState {
  /** the order this visit created — a retry after a declined card pays the SAME order */
  order: {
    documentId: string;
    clientTotal: number;
    /** the server's hold facts, kept so re-entry stays held */
    held?: HeldOrderFacts;
    /** an M4 reservation as last read from the server, kept so re-entry stays a reservation (no card, no second order) */
    reserved?: ReservationFacts;
    /** the create replayed an order already past its draft: no authorised outcome without `cardAuthorized` */
    cardEvidenceRequired?: boolean;
  } | null;
  /** the amount the Payment Element was sized with */
  elementAmountCents: number;
  /** set once `add_payment_info` has been offered this visit — a retry after a decline never offers it again */
  paymentInfoOffered?: boolean;
}

interface Refusal { booked: false; reason: string }

/**
 * THE IN-PAGE PAY SEQUENCE. Each step runs only if every earlier one succeeded, so a refusal at any point
 * is honest about the money: before step 5 no card was sent to Stripe; a step-5 error means the card was
 * not authorized; after step 5 only the server's status decides. Mutates `state` (order + element amount).
 *
 * THE SERVER IS ASKED BEFORE THE CARD. A reload replays the attempt key, so `createOrder` may hand back an
 * earlier visit's order whose card was already authorized — and after a reload the Payment Element is
 * EMPTY, so validating it first would refuse the very resume this sequence exists for. The card (and the
 * element's size) is therefore checked only once both the order's signed-webhook status (step 2) and the
 * intent's own Stripe status (step 4) say it is still payable. Anything already handed off waits on the
 * server and is never confirmed a second time.
 *
 * THE ATTEMPT KEY OUTLIVES THE HAND-OFF. It is ended only when the server settles on pending_acceptance /
 * paid — never when `confirmPayment` resolves, and never on a refusal, a timeout, a failure or a closed
 * order. Every uncertain state therefore replays the SAME key and the SAME order.
 */
export async function runInpagePayment({
  address: rawAddress,
  lineCount,
  state,
  deps,
}: {
  address: unknown;
  lineCount: number;
  state: InpagePaymentState;
  deps: InpagePaymentDeps;
}): Promise<InpageResult | Refusal> {
  // 1 — the server-priced order, created once per visit (a reload replays the attempt key → the SAME order)
  let replayable = false;
  // A held order (this instance, re-entered) stays held: before the replay read, the intent, the card, Stripe, the attempt and GA4.
  if (state.order?.held) return heldOutcome(state.order.held);
  // An M4 reservation (this instance, re-entered) stays a reservation: no intent, no card, no Stripe, no attempt end, no GA4.
  if (state.order?.reserved) return reservationOutcome(state.order.reserved);
  if (!state.order) {
    const created = await deps.createOrder();
    if (!created.ok) return { booked: false, reason: created.reason };
    state.order = {
      documentId: created.documentId,
      clientTotal: created.clientTotal,
      ...(created.held ? { held: created.held } : {}),
      ...(created.cardEvidenceRequired ? { cardEvidenceRequired: true } : {}),
    };
    // The authoritative create response said "held" (also on an idempotent replay): stop here, whatever a status read would say.
    if (created.held) return heldOutcome(created.held);
    // M4 · the server's plan said pay at the shop and the create returned an M4 status: stop before every payment step. A fresh
    // reservation is `pending_acceptance`; a replay of a later one is judged from ONE server read, and anything else is held.
    if (created.reserved) {
      const { plan, orderStatus } = created.reserved;
      const status = orderStatus === "pending_acceptance" ? "requested" : reservationVerdict(await deps.readStatus(created.documentId), plan);
      if (!status) return RESERVATION_UNCONFIRMED;
      state.order.reserved = { plan, status };
      return reservationOutcome(state.order.reserved);
    }
    replayable = true;
  }
  const order = state.order;
  const poll = () => pollServer({ order, lineCount, deps });

  // 2 — the order createOrder returned may be an earlier visit's, already carrying its card: ask the server first
  if (replayable) {
    const prior = await deps.readStatus(order.documentId);
    // the replayed order moved on with no card attached (a reservation made under a stale card quote): held, never authorised
    if (cardEvidenceMissing(prior)) return CARD_UNCONFIRMED;
    const phase = orderPhase(prior?.orderStatus);
    if (prior && (phase === "paid" || phase === "awaiting_acceptance" || phase === "in_flight")) return poll();
    if (prior && phase === "closed") {
      // the same money-aware adjudication as every status check (a refunded deposit is said as refunded, never "nothing was charged")
      const verdict = adjudicateMoney(judgeStatus({ status: prior.orderStatus, fulfillment: null, orderRef: order.documentId, paid: "", items: lineCount, sightings: 0, attempt: 1 }), prior);
      if (verdict.kind !== "keep-polling") return verdict.result;
    }
  }

  // 3 — billing address (F9): the backend attaches it to the Stripe customer while creating the intent
  const address = billingAddress(rawAddress);
  if (!address) return { booked: false, reason: `Add the billing country and postal code under Payment details. ${NOT_CHARGED}` };
  const intent = await deps.requestIntent(order.documentId, address);
  if (!intent.ok) return { booked: false, reason: intent.reason };
  if (!amountMatches(intent.amountCents, order.clientTotal)) {
    return { booked: false, reason: `The amount prepared for your card doesn't match the $${order.clientTotal.toFixed(2)} shown, so it was not submitted. ${NOT_CHARGED}` };
  }

  // 4 — the backend reuses a live intent for a retried order: one already holding a card is not confirmed again
  if (HANDED_OFF.includes((await deps.intentStatus(intent.clientSecret)) ?? "")) return poll();

  // 5 — a still-payable intent: size the element to the server's amount, require a complete card, hand it to
  //     Stripe. Resolving is a hand-off, not an outcome — and it does NOT end the attempt.
  const cents = intent.amountCents as number;
  if (cents !== state.elementAmountCents) {
    const resizeError = await deps.resizeElement(cents);
    if (resizeError) return { booked: false, reason: `${resizeError} ${NOT_CHARGED}` };
    state.elementAmountCents = cents;
  } else {
    const cardError = await deps.validateCard();
    if (cardError) return { booked: false, reason: `${cardError} ${NOT_CHARGED}` };
  }
  // measurement only: offered once per visit, after the card is validated and before a NEW confirm; it can
  // neither throw into nor wait on the payment
  if (!state.paymentInfoOffered) {
    state.paymentInfoOffered = true;
    try {
      deps.emitPaymentInfo?.();
    } catch {
      /* measurement never changes the payment */
    }
  }
  const confirmError = await deps.confirm(intent.clientSecret, order.documentId);
  if (confirmError) return { booked: false, reason: `${confirmError} ${NOT_CHARGED}` };

  // 6 — only the server's status ends this
  return poll();
}

/** Step 5: poll the signed-webhook status; the attempt ends — and the GA4 purchase is offered — only on a
 *  settled pending_acceptance / paid. */
async function pollServer({
  order,
  lineCount,
  deps,
}: {
  order: { documentId: string; clientTotal: number | null; cardEvidenceRequired?: boolean };
  lineCount: number;
  deps: Pick<InpagePaymentDeps, "sleep" | "readStatus" | "endAttempt" | "emitPurchase">;
}): Promise<InpageResult> {
  let sightings = 0;
  for (let attempt = 1; attempt <= POLL_ATTEMPTS; attempt += 1) {
    await deps.sleep(attempt === 1 ? 800 : POLL_INTERVAL_MS);
    const read = await deps.readStatus(order.documentId);
    if (!read) continue;
    if (read.orderStatus === "pending_acceptance") sightings += 1;
    const verdict = judgeRead({ order, read, lineCount, sightings, attempt, deps });
    if (verdict.kind !== "keep-polling") return verdict.result;
  }
  return TIMED_OUT;
}

const MONEY_UNCONFIRMED = {
  kicker: "Payment not confirmed yet",
  lead: "We can't confirm this payment or this booking right now. Do not pay again — check My Trips.",
};

/**
 * WO-PAYMENTS-PHASE1-001 (Light P2) · the ONE money-aware adjudication of a status verdict, used by every path that reads an order:
 * the poll, "Check payment status" and the replay of an order that already closed. It only re-words what the status verdict decided,
 * from the server's own facts:
 *   · exact deposit facts → the money sentence (depositStatus + flags) and the separate fulfillment sentence; `pending` (authorised,
 *     not charged) only when the money IS an active authorisation, `awaiting` until the shop has confirmed, and a closed or declined
 *     (not re-offered) booking is said as closed, never as a confirmation;
 *   · a projection that failed or is not exactly the contract → money unknown and the booking withheld (`awaiting`), and a closed
 *     order makes no "nothing was charged" claim; so does a card read whose projection is an exact pay-at-shop order (no card can
 *     belong to it: never a card hold, a payment or a booking confirmation from the `kind` tag);
 *   · anything else (exact M3, no payment block, a read that predates the projection) → the verdict exactly as before.
 * keep-polling always stays keep-polling.
 */
export function adjudicateMoney(verdict: PollVerdict, read: OrderStatusRead | null | undefined): PollVerdict {
  if (verdict.kind === "keep-polling" || !read) return verdict;
  const projection = read.paymentProjection;
  const facts = projection === undefined || projection === "deposit" ? depositFactsOf(read) : null;
  const settled = verdict.kind === "settled" && "authorized" in verdict.result ? verdict.result : null;
  const retry = "retry" in verdict.result && verdict.result.retry;
  if (facts) {
    const copy = depositReceiptCopy(facts);
    const money = MONEY_OF[facts.depositStatus];
    const closed = facts.fulfillmentStatus === "closed" || (facts.fulfillmentStatus === "declined" && !facts.reoffered);
    if (settled && !closed) {
      const awaiting = facts.fulfillmentStatus !== "confirmed" || money === "unknown";
      return {
        kind: "settled",
        result: { ...settled, kicker: copy.kicker, lead: copy.lead, pending: awaiting && money === "authorised", awaiting, money },
      };
    }
    return { kind: "failed", result: { booked: false, retry, reason: `${copy.kicker}. ${copy.lead}` } };
  }
  if (projection === "invalid" || projection === "unavailable" || projection === "deposit" || projection === "pay_at_shop") {
    if (settled) {
      return { kind: "settled", result: { ...settled, ...MONEY_UNCONFIRMED, pending: false, awaiting: true, money: "unknown" } };
    }
    if (!retry) {
      return { kind: "failed", result: { booked: false, retry: false, reason: `This order is ${read.orderStatus ?? "closed"}. ${MONEY_UNCONFIRMED.lead}` } };
    }
  }
  return verdict;
}

/** One server read, judged; a settled read ends the attempt and offers the GA4 purchase (once per order id). */
function judgeRead({
  order,
  read,
  lineCount,
  sightings,
  attempt,
  deps,
}: {
  order: { documentId: string; clientTotal: number | null; cardEvidenceRequired?: boolean };
  read: OrderStatusRead;
  lineCount: number;
  sightings: number;
  attempt: number;
  deps: Pick<InpagePaymentDeps, "endAttempt" | "emitPurchase">;
}): PollVerdict {
  // never settled as authorised without the server's card evidence, on EVERY entry path: held, the attempt kept, no cart change, nothing emitted
  if (cardEvidenceMissing(read)) return { kind: "failed", result: CARD_UNCONFIRMED };
  const verdict = judgeStatus({
    status: read.orderStatus,
    fulfillment: read.fulfillment,
    orderRef: read.legacyId === "" ? order.documentId : (read.legacyId ?? order.documentId),
    paid: order.clientTotal === null ? "" : `$${order.clientTotal.toFixed(2)}`,
    items: serverItemCount(read) ?? lineCount, // W17 — the order's own lines when the server sends them, not today's basket
    sightings,
    attempt,
  });
  const judged = adjudicateMoney(verdict, read);
  if (judged.kind === "settled") {
    deps.endAttempt();
    const purchase = inpagePurchase(order.documentId, read);
    if (purchase) {
      try {
        deps.emitPurchase?.(purchase);
      } catch {
        /* measurement never changes what the customer is told about their order */
      }
    }
  }
  return judged;
}

/**
 * W17 — what an order covered, saved under ITS order id when the order is created (per tab: a redirect return or a reload
 * in the same tab finds it). Recovery settles that order against this record — never against the basket as it is now.
 * No record means the cart's ownership is unknown: the cart is left exactly as it is.
 */
export interface OrderCartSnapshot {
  /** Partial checkout: full cart plus selected request identity at order creation. */
  checkoutBinding?: string;
  documentId: string;
  /** the checkout attempt that created the order — only that attempt may be ended when the order settles */
  attemptId: string | null;
  clientTotal: number | null;
  lines: DesignCartLine[];
  host: { id: string; qty: number }[];
  /** set once the carts have been settled for this order (recovery or the in-page success): never settled twice */
  settled?: boolean;
  /** M4 · the server's pay-at-shop plan the order was created under: a reload judges the order as a reservation, never a card */
  payment?: PayAtShopPlan;
}

const SNAPSHOT_KEY = (documentId: string) => `sg.order-cart.v1:${documentId}`;
const SNAPSHOT_TTL_MS = 24 * 60 * 60 * 1000;
type SnapshotStorage = Pick<Storage, "getItem" | "setItem" | "removeItem"> | null | undefined;

/** Saves the record for an order once: a replayed attempt that hands back the same order keeps the first record. */
export function saveOrderSnapshot(storage: SnapshotStorage, snapshot: OrderCartSnapshot): OrderCartSnapshot {
  const existing = readOrderSnapshot(storage, snapshot.documentId);
  if (existing) return existing;
  try {
    storage?.setItem(SNAPSHOT_KEY(snapshot.documentId), JSON.stringify({ v: 1, at: Date.now(), snapshot }));
  } catch {
    /* storage unavailable: the caller keeps the in-memory copy for this page */
  }
  return snapshot;
}

/**
 * Marks an order's record settled, keeping its count / total / attempt metadata and its creation time. Repeated status
 * checks or a reload of the same `?sg_order=` address then read the record but change no cart. Returns the marked record.
 */
export function markOrderSnapshotSettled(storage: SnapshotStorage, snapshot: OrderCartSnapshot): OrderCartSnapshot {
  const settled = { ...snapshot, settled: true };
  try {
    const raw = JSON.parse(storage?.getItem(SNAPSHOT_KEY(snapshot.documentId)) ?? "null") as { v?: number; at?: number } | null;
    const at = raw?.v === 1 && typeof raw.at === "number" ? raw.at : Date.now();
    storage?.setItem(SNAPSHOT_KEY(snapshot.documentId), JSON.stringify({ v: 1, at, snapshot: settled }));
  } catch {
    /* storage unavailable: the caller keeps the marked in-memory copy for this page */
  }
  return settled;
}

export function readOrderSnapshot(storage: SnapshotStorage, documentId: string): OrderCartSnapshot | null {
  try {
    const raw = JSON.parse(storage?.getItem(SNAPSHOT_KEY(documentId)) ?? "null") as { v?: number; at?: number; snapshot?: OrderCartSnapshot } | null;
    const snap = raw?.snapshot;
    if (raw?.v !== 1 || typeof raw.at !== "number" || Date.now() - raw.at > SNAPSHOT_TTL_MS || snap?.documentId !== documentId) return null;
    if (!Array.isArray(snap.lines) || !Array.isArray(snap.host)) return null;
    // a stored plan that is not exactly the M4 plan is no record at all: the order's mode is then unknown, never assumed
    if (snap.payment !== undefined && !isPayAtShopPlan(snap.payment)) return null;
    return snap;
  } catch {
    return null;
  }
}

/**
 * W17 — the newest UNSETTLED record an order created under checkout attempt `attemptId` left in this tab, or null. Read-only:
 * it lets a reload reconcile that order's status before any field is asked for, without creating or replaying anything.
 */
export function findAttemptSnapshot(storage: Pick<Storage, "getItem" | "setItem" | "removeItem" | "key" | "length"> | null | undefined, attemptId: string | null): OrderCartSnapshot | null {
  if (!storage || !attemptId) return null;
  let best: { at: number; snapshot: OrderCartSnapshot } | null = null;
  try {
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (!key?.startsWith(SNAPSHOT_KEY(""))) continue;
      const snapshot = readOrderSnapshot(storage, key.slice(SNAPSHOT_KEY("").length));
      if (!snapshot || snapshot.settled || snapshot.attemptId !== attemptId) continue;
      const at = (JSON.parse(storage.getItem(key) ?? "null") as { at?: number } | null)?.at ?? 0;
      if (!best || at > best.at) best = { at, snapshot };
    }
  } catch {
    return null;
  }
  return best?.snapshot ?? null;
}

/** The order's own item count from the server's lines (quantities), or null when the read carries none usable. */
export function serverItemCount(read: OrderStatusRead | null | undefined): number | null {
  const items = read?.items;
  if (!items?.length || !items.every((i) => Number.isInteger(i.quantity) && i.quantity > 0)) return null;
  return items.reduce((n, i) => n + i.quantity, 0);
}

/**
 * The design cart left once a recovered order settles: every line added after that order stays, and a line whose
 * quantity grew keeps the extra. Only what the order covered leaves the cart.
 */
export function cartAfterSettledOrder(snapshot: Pick<OrderCartSnapshot, "lines">, current: DesignCartLine[]): DesignCartLine[] {
  const covered = new Map<string, number>();
  for (const l of snapshot.lines) covered.set(l.id, (covered.get(l.id) ?? 0) + (Number(l.qty) || 0));
  const left: DesignCartLine[] = [];
  for (const l of current) {
    const qty = Number(l.qty) || 0;
    const used = Math.min(covered.get(l.id) ?? 0, qty);
    covered.set(l.id, (covered.get(l.id) ?? 0) - used);
    if (qty - used > 0) left.push(used ? { ...l, qty: qty - used } : l);
  }
  return left;
}

/**
 * Both carts after a recovered order settles. With the order's record: the design lines it covered leave (later lines and
 * extra quantity stay) and each host line loses exactly the quantity the order covered. Without one, or with a record
 * already settled (a repeated status check, a reload of the same return address): nothing changes.
 */
export function cartsAfterSettledOrder(
  snapshot: OrderCartSnapshot | null,
  current: { lines: DesignCartLine[]; host: { id: string; qty: number }[] },
): { remainingCart: DesignCartLine[]; hostRemove: string[]; hostQty: [string, number][] } {
  if (!snapshot || snapshot.settled) return { remainingCart: current.lines, hostRemove: [], hostQty: [] };
  const covered = new Map(snapshot.host.map((h) => [h.id, h.qty]));
  const hostRemove: string[] = [];
  const hostQty: [string, number][] = [];
  for (const h of current.host) {
    const used = covered.get(h.id);
    if (used === undefined) continue; // added after the order
    if (h.qty > used) hostQty.push([h.id, h.qty - used]);
    else hostRemove.push(h.id);
  }
  return { remainingCart: cartAfterSettledOrder(snapshot, current.lines), hostRemove, hostQty };
}

/**
 * W17-FORM-ERRORS-NOTIFICATIONS-20261019 — "Check payment status" for an order whose outcome is uncertain: ONE read of the
 * signed-webhook status, judged exactly as the poll judges it. It never creates an order, confirms a card or replays a
 * payment. `waiting` means the server still has not settled — the caller keeps its do-not-pay-again lock.
 */
export async function checkPaymentStatus({
  orderId,
  lineCount,
  clientTotal,
  deps,
}: {
  orderId: string;
  lineCount: number;
  clientTotal: number | null;
  deps: Pick<InpagePaymentDeps, "readStatus" | "endAttempt" | "emitPurchase">;
}): Promise<InpageResult | { waiting: true }> {
  const read = await deps.readStatus(orderId);
  if (!read) return { waiting: true };
  const verdict = judgeRead({
    order: { documentId: orderId, clientTotal },
    read,
    lineCount,
    sightings: read.orderStatus === "pending_acceptance" ? 1 : 0,
    attempt: POLL_ATTEMPTS, // a manual check is the last word for now: no landing grace, the customer may check again
    deps,
  });
  return verdict.kind === "keep-polling" ? { waiting: true } : verdict.result;
}

/**
 * FOUNDER RULING 2026-09-26 — a customer back on the review page after a redirect-method payment
 * (`?sg_order=<id>`; Stripe adds `redirect_status`). Same rule as everywhere else in this file: the SERVER's
 * status ends the wait and only the server can say authorised/paid. `redirect_status` is a client-side
 * result — `failed` is safe to show (it cannot invent a settlement) and `succeeded` is only a reason to
 * poll, never an outcome.
 */
export async function resumeAfterRedirect({
  orderId,
  redirectStatus,
  lineCount,
  displayedTotal,
  deps,
}: {
  orderId: string;
  redirectStatus: string | null;
  lineCount: number;
  displayedTotal: number;
  deps: Pick<InpagePaymentDeps, "sleep" | "readStatus" | "endAttempt" | "emitPurchase">;
}): Promise<InpageResult> {
  if (redirectStatus === "failed") {
    return {
      booked: false,
      retry: true,
      reason: `Your bank or payment provider sent you back without confirming the payment, so this order has not been paid. ${NOT_CHARGED} You can try again.`,
    };
  }
  // No basket on this visit (storage cleared) means no total to print — the done copy then omits it, not "$0.00".
  const clientTotal = Number.isFinite(displayedTotal) && displayedTotal > 0 ? displayedTotal : null;
  return pollServer({ order: { documentId: orderId, clientTotal }, lineCount, deps });
}
